import React from "react";
import { FormControl, FormLabel, Select, MenuItem, TextField, Checkbox, FormControlLabel } from "@mui/material";
import { useNode, useEditor } from "@craftjs/core";

export const Layout = ({
  name,
  background,
  padding = 0,
  gap = 0,
  justifyContent = 'flex-start',
  alignItems = 'flex-start',
  width,
  height,
  minWidth,
  maxWidth,
  minHeight,
  maxHeight,
  flex,
  flexGrow,
  flexShrink = 1,
  flexBasis,
  overflowX = 'visible',
  overflowY = 'visible',
  flexDirection = 'column',
  canvas = false,
  hidden,
  style,
  children,
  ...props
}) => {
  const { 
    connectors: { connect, drag },
    isSelected,
    isHovered
  } = useNode((state) => ({
    isSelected: state.events.selected,
    isHovered: state.events.hovered,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled
  }));

  if (hidden) return null;

  const formatSize = (value) => {
    if (!value) return 'auto';
    if (typeof value === 'string') return value;
    return `${value}px`;
  };

  const formatPadding = (value) => {
    if (!value) return '0px';
    if (typeof value === 'string') return value;
    return `${value}px`;
  };

  const formatGap = (value) => {
    if (!value) return '0px';
    if (typeof value === 'string') return value;
    return `${value}px`;
  };

  // Selection styling
  const getSelectionStyle = () => {
    if (!enabled) return {};
    
    if (isSelected) {
      return {
        outline: '1px dashed #2196f3',
        outlineOffset: '2px',
        backgroundColor: background ? background : 'rgba(33, 150, 243, 0.05)'
      };
    }
    if (isHovered) {
      return {
        outline: '1px dashed #ff9800',
        outlineOffset: '1px',
        backgroundColor: background ? background : 'rgba(255, 152, 0, 0.03)'
      };
    }
    return {};
  };

  return (
    <div
      ref={ref => enabled ? connect(drag(ref)) : connect(ref)}
      name={name}
      style={{
        background,
        padding: formatPadding(padding),
        gap: formatGap(gap),
        display: 'flex',
        flexDirection: flexDirection || 'column',
        justifyContent,
        alignItems,
        width: formatSize(width),
        height: formatSize(height),
        minWidth: formatSize(minWidth),
        maxWidth: formatSize(maxWidth),
        minHeight: minHeight ? formatSize(minHeight) : (canvas ? '50px' : 'unset'),
        maxHeight: maxHeight ? formatSize(maxHeight) : (height ? formatSize(height) : 'none'),
        flex: flex,
        flexGrow: flexGrow,
        flexShrink: flexShrink,
        flexBasis: flexBasis,
        border: 'none',
        margin: 0,
        boxSizing: 'border-box',
        overflowX,
        overflowY,
        transition: 'all 0.2s ease',
        position: 'relative',
        ...style,
        ...getSelectionStyle()
      }}
      {...props}
    >
      {children}
    </div>
  );
};

export const LayoutSettings = () => {
  const {
    name,
    background,
    padding,
    gap,
    justifyContent,
    alignItems,
    width,
    height,
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
    flex,
    flexGrow,
    flexShrink,
    flexBasis,
    overflowX,
    overflowY,
    flexDirection,
    canvas,
    hidden,
    actions: { setProp }
  } = useNode(node => ({
    name: node.data.props.name,
    background: node.data.props.background,
    padding: node.data.props.padding,
    gap: node.data.props.gap,
    justifyContent: node.data.props.justifyContent,
    alignItems: node.data.props.alignItems,
    width: node.data.props.width,
    height: node.data.props.height,
    minWidth: node.data.props.minWidth,
    maxWidth: node.data.props.maxWidth,
    minHeight: node.data.props.minHeight,
    maxHeight: node.data.props.maxHeight,
    flex: node.data.props.flex,
    flexGrow: node.data.props.flexGrow,
    flexShrink: node.data.props.flexShrink,
    flexBasis: node.data.props.flexBasis,
    overflowX: node.data.props.overflowX,
    overflowY: node.data.props.overflowY,
    flexDirection: node.data.props.flexDirection,
    canvas: node.data.props.canvas,
    hidden: node.data.props.hidden
  }));

  const [localPadding, setLocalPadding] = React.useState(padding);
  const [localGap, setLocalGap] = React.useState(gap);
  const [localWidth, setLocalWidth] = React.useState(width);
  const [localHeight, setLocalHeight] = React.useState(height);
  const [localMinWidth, setLocalMinWidth] = React.useState(minWidth);
  const [localMaxWidth, setLocalMaxWidth] = React.useState(maxWidth);
  const [localMinHeight, setLocalMinHeight] = React.useState(minHeight);
  const [localMaxHeight, setLocalMaxHeight] = React.useState(maxHeight);

  React.useEffect(() => setLocalPadding(padding), [padding]);
  React.useEffect(() => setLocalGap(gap), [gap]);
  React.useEffect(() => setLocalWidth(width), [width]);
  React.useEffect(() => setLocalHeight(height), [height]);
  React.useEffect(() => setLocalMinWidth(minWidth), [minWidth]);
  React.useEffect(() => setLocalMaxWidth(maxWidth), [maxWidth]);
  React.useEffect(() => setLocalMinHeight(minHeight), [minHeight]);
  React.useEffect(() => setLocalMaxHeight(maxHeight), [maxHeight]);

  const handleSizeChange = (prop, value) => {
    if (value && !value.includes('%') && !value.includes('px') && !value.includes('rem') && !value.includes('em') && value !== 'auto') {
      value = `${value}px`;
    }
    setProp(props => props[prop] = value);
  };

  const handleFlexChange = (prop, value) => {
    if (value === '') {
      setProp(props => props[prop] = undefined);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        setProp(props => props[prop] = numValue);
      }
    }
  };

  const isHorizontal = flexDirection === 'row';

  return (
    <div>
      <FormControl fullWidth margin="normal">
        <FormLabel>Component Name</FormLabel>
        <TextField
          value={name || ''}
          onChange={(e) => setProp(props => props.name = e.target.value)}
          size="small"
          placeholder="Enter component name"
        />
      </FormControl>

      <FormControlLabel
        control={
          <Checkbox
            checked={canvas}
            onChange={(e) => setProp(props => props.canvas = e.target.checked)}
          />
        }
        label="Act as container (can hold other elements)"
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={hidden || false}
            onChange={(e) => setProp(props => props.hidden = e.target.checked)}
          />
        }
        label="Hidden"
      />

      <FormControl fullWidth margin="normal" component="fieldset">
        <FormLabel component="legend">Background</FormLabel>
        <input
          type="color"
          value={background || '#ffffff'}
          onChange={(e) => setProp(props => props.background = e.target.value)}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Direction</FormLabel>
        <Select
          value={flexDirection || 'column'}
          onChange={(e) => setProp(props => props.flexDirection = e.target.value)}
          size="small"
        >
          <MenuItem value="column">Vertical</MenuItem>
          <MenuItem value="row">Horizontal</MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Padding</FormLabel>
        <TextField
          value={localPadding || 0}
          onChange={(e) => setLocalPadding(e.target.value)}
          onBlur={(e) => handleSizeChange('padding', e.target.value)}
          size="small"
          placeholder="0, 10px, 1rem, 5%"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Gap</FormLabel>
        <TextField
          value={localGap || 0}
          onChange={(e) => setLocalGap(e.target.value)}
          onBlur={(e) => handleSizeChange('gap', e.target.value)}
          size="small"
          placeholder="0, 10px, 1rem, 5%"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Width</FormLabel>
        <TextField
          value={localWidth || ''}
          onChange={(e) => setLocalWidth(e.target.value)}
          onBlur={(e) => handleSizeChange('width', e.target.value)}
          size="small"
          placeholder="auto, 300px, 100%, or leave empty"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Height</FormLabel>
        <TextField
          value={localHeight || ''}
          onChange={(e) => setLocalHeight(e.target.value)}
          onBlur={(e) => handleSizeChange('height', e.target.value)}
          size="small"
          placeholder="auto, 200px, 50%, or leave empty"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Min Width</FormLabel>
        <TextField
          value={localMinWidth || ''}
          onChange={(e) => setLocalMinWidth(e.target.value)}
          onBlur={(e) => handleSizeChange('minWidth', e.target.value)}
          size="small"
          placeholder="auto, 100px, 10%, or leave empty"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Max Width</FormLabel>
        <TextField
          value={localMaxWidth || ''}
          onChange={(e) => setLocalMaxWidth(e.target.value)}
          onBlur={(e) => handleSizeChange('maxWidth', e.target.value)}
          size="small"
          placeholder="auto, 500px, 90%, or leave empty"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Min Height</FormLabel>
        <TextField
          value={localMinHeight || ''}
          onChange={(e) => setLocalMinHeight(e.target.value)}
          onBlur={(e) => handleSizeChange('minHeight', e.target.value)}
          size="small"
          placeholder="auto, 50px, 10%, or leave empty"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Max Height</FormLabel>
        <TextField
          value={localMaxHeight || ''}
          onChange={(e) => setLocalMaxHeight(e.target.value)}
          onBlur={(e) => handleSizeChange('maxHeight', e.target.value)}
          size="small"
          placeholder="auto, 300px, 80%, or leave empty"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Flex</FormLabel>
        <TextField
          value={flex || ''}
          onChange={(e) => handleFlexChange('flex', e.target.value)}
          size="small"
          placeholder="1, 0 1 auto, or leave empty"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Flex Grow</FormLabel>
        <TextField
          value={flexGrow || ''}
          onChange={(e) => handleFlexChange('flexGrow', e.target.value)}
          size="small"
          placeholder="0, 1, 2, or leave empty"
          type="number"
          inputProps={{ min: 0, step: 1 }}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Flex Shrink</FormLabel>
        <TextField
          value={flexShrink !== undefined ? flexShrink : ''}
          onChange={(e) => handleFlexChange('flexShrink', e.target.value)}
          size="small"
          placeholder="1 (default), 0, or leave empty"
          type="number"
          inputProps={{ min: 0, step: 1 }}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Flex Basis</FormLabel>
        <TextField
          value={flexBasis || ''}
          onChange={(e) => setProp(props => props.flexBasis = e.target.value || undefined)}
          size="small"
          placeholder="auto, 200px, 50%, or leave empty"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Horizontal Scroll</FormLabel>
        <Select
          value={overflowX || 'visible'}
          onChange={(e) => setProp(props => props.overflowX = e.target.value)}
          size="small"
        >
          <MenuItem value="visible">No scroll</MenuItem>
          <MenuItem value="hidden">Hidden</MenuItem>
          <MenuItem value="scroll">Scroll</MenuItem>
          <MenuItem value="auto">Auto</MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Vertical Scroll</FormLabel>
        <Select
          value={overflowY || 'visible'}
          onChange={(e) => setProp(props => props.overflowY = e.target.value)}
          size="small"
        >
          <MenuItem value="visible">No scroll</MenuItem>
          <MenuItem value="hidden">Hidden</MenuItem>
          <MenuItem value="scroll">Scroll</MenuItem>
          <MenuItem value="auto">Auto</MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>{isHorizontal ? 'Vertical Alignment' : 'Horizontal Alignment'}</FormLabel>
        <Select
          value={alignItems || 'flex-start'}
          onChange={(e) => setProp(props => props.alignItems = e.target.value)}
          size="small"
        >
          <MenuItem value="flex-start">{isHorizontal ? 'Top' : 'Left'}</MenuItem>
          <MenuItem value="center">Center</MenuItem>
          <MenuItem value="flex-end">{isHorizontal ? 'Bottom' : 'Right'}</MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>{isHorizontal ? 'Horizontal Alignment' : 'Vertical Alignment'}</FormLabel>
        <Select
          value={justifyContent || 'flex-start'}
          onChange={(e) => setProp(props => props.justifyContent = e.target.value)}
          size="small"
        >
          <MenuItem value="flex-start">{isHorizontal ? 'Left' : 'Top'}</MenuItem>
          <MenuItem value="center">Center</MenuItem>
          <MenuItem value="flex-end">{isHorizontal ? 'Right' : 'Bottom'}</MenuItem>
        </Select>
      </FormControl>
    </div>
  );
};

export const LayoutDefaultProps = {
  name: "Layout",
  padding: 0,
  gap: 0,
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  width: null,
  height: null,
  minWidth: null,
  maxWidth: null,
  minHeight: null,
  maxHeight: null,
  flex: null,
  flexGrow: null,
  flexShrink: 1,
  flexBasis: null,
  overflowX: 'visible',
  overflowY: 'visible',
  flexDirection: 'column',
  canvas: false,
  hidden: false
};

Layout.craft = {
  props: LayoutDefaultProps,
  related: {
    settings: LayoutSettings
  },
  rules: {
    canDrag: () => true,
    canDrop: (node, target) => {
      return target.data.props.canvas;
    },
    canMoveIn: (incomingNodes, currentNode, helper) => {
      return currentNode.data.props.canvas;
    }
  },
  isCanvas: true,
  displayName: 'Layout'
};